/**
 * Reference data for the waste-determination basis wizard (WasteStreamBasisHelperDialog.vue):
 * EPA F-list and K-list listed-hazardous-waste processes, plus the P-list and U-list discarded
 * commercial chemical products, per 40 CFR 261.31/261.32/261.33. Each entry carries the wizard's
 * self-certification prompt, qualifying criteria, and example chemicals alongside the code itself,
 * not just a code-to-description lookup - kept separate from hazwaste-shared/waste-codes.ts, which
 * is the flat reference catalog used for code selection/autocomplete elsewhere.
 */
export interface FProcess {
  id: string;
  title: string;
  codes: string[];
  cfr: string;
  prompt: string;
  description: string;
  chemicals: string;
  criteria: string[];
  note?: string;
}
export const F_LIST_PROCESSES: FProcess[] = [
  {
    id: 'f001',
    title: 'Halogenated solvent degreasing',
    codes: ['F001'],
    cfr: '§261.31 F001',
    prompt: 'Is this waste from halogenated solvents used specifically to degrease metal parts or equipment?',
    description: 'Spent halogenated solvents used in degreasing operations, plus still bottoms from their recovery.',
    chemicals:
      'Tetrachloroethylene (PCE), trichloroethylene (TCE), methylene chloride (DCM), 1,1,1-trichloroethane, carbon tetrachloride, chlorinated fluorocarbons/Freon',
    criteria: [
      'The solvent was used specifically for DEGREASING — vapor degreasing, cold cleaning, or similar metal-cleaning operations',
      'The solvent is now SPENT — exhausted and no longer fit for its original degreasing purpose',
      'Includes still bottoms from the recovery/recycling of these spent solvents',
      'MIXTURE RULE: If the original (fresh) solvent contained ≥10% of these F001 solvents by volume, the entire spent mixture is F001 — even if other solvents make up the majority',
      'NOT F001 if these solvents were used in a non-degreasing application — that would be F002 instead',
    ],
  },
  {
    id: 'f002',
    title: 'Other halogenated solvent use (non-degreasing)',
    codes: ['F002'],
    cfr: '§261.31 F002',
    prompt:
      'Were halogenated solvents used in this process for something other than degreasing — cleaning (non-parts), chemical reactions, extraction, lab work, or formulation?',
    description:
      'Spent halogenated solvents used in any application other than degreasing, plus still bottoms from recovery.',
    chemicals:
      'All F001 solvents + chlorobenzene, 1,1,2-trichloroethane, CFC-113 (1,1,2-trichloro-1,2,2-trifluoroethane), o-dichlorobenzene, trichlorofluoromethane (Freon 11)',
    criteria: [
      'The listed halogenated solvents were used in an application OTHER THAN degreasing (e.g., general cleaning, chemical production, extraction, lab processes)',
      'The solvent is now SPENT — no longer serving its original purpose',
      'Includes still bottoms from recovery of these spent solvents',
      'MIXTURE RULE: ≥10% of any listed F002 solvent before use by volume triggers the listing for the entire mixture',
      'IMPORTANT: The same chemical generates F001 if used in degreasing, F002 if used in other operations — the use context determines the code',
    ],
    note: 'F002 covers a wider range of halogenated solvents than F001. If your degreasing solvent is listed only in F002 (e.g., chlorobenzene), it generates F002 — not F001 — even in degreasing.',
  },
  {
    id: 'f003',
    title: 'Spent non-halogenated solvents — Group A',
    codes: ['F003'],
    cfr: '§261.31 F003',
    prompt: 'Did this process use any of these common non-chlorinated solvents, and are they now spent?',
    description:
      'Very common industrial, manufacturing, and lab solvents when spent — the most frequently encountered F-code.',
    chemicals:
      'Acetone, xylene, methyl isobutyl ketone (MIBK), n-butyl alcohol, cyclohexanone, methanol, ethyl acetate, ethyl benzene, ethyl ether',
    criteria: [
      'The solvent was used for its solvent properties (cleaning, thinning, extraction, formulating, etc.) and is now SPENT',
      'Must have been used as a solvent — if acetone, MEK, etc. are generated as process byproducts rather than used as solvents, F003 may not apply',
      'Includes still bottoms from recovery of these spent solvents',
      'MIXTURE RULE (pure F003 mixture): A mixture of only F003 solvents = F003',
      'MIXTURE RULE (mixed with other F-codes): If the mixture also contained ≥10% of F001, F002, F004, or F005 solvents before use, the mixture carries all applicable codes',
    ],
  },
  {
    id: 'f004',
    title: 'Spent cresols / nitrobenzene solvents',
    codes: ['F004'],
    cfr: '§261.31 F004',
    prompt: 'Were cresols, cresylic acid, or nitrobenzene used as solvents in this process and are they now spent?',
    description: 'Cresol-based and nitrobenzene solvents when spent.',
    chemicals: 'Cresols (o-, m-, p-cresol), cresylic acid (mixed cresols/xylenols from coal tar), nitrobenzene',
    criteria: [
      'Cresols or nitrobenzene were used AS SOLVENTS (not just present as contaminants in other wastes)',
      'They are now SPENT — no longer useful for their intended solvent purpose',
      'Includes still bottoms from recovery',
      'MIXTURE RULE: ≥10% F004 solvent content (by volume) before use applies the listing to the entire mixture',
      'Cresylic acid from coal tar refining operations is a K-list issue (K087/K141) — make sure this is a USED/SPENT solvent, not a process residue',
    ],
  },
  {
    id: 'f005',
    title: 'Spent non-halogenated solvents — Group B',
    codes: ['F005'],
    cfr: '§261.31 F005',
    prompt: 'Were toluene, MEK, or other Group B solvents used in this process and are they now spent?',
    description: 'Additional common non-chlorinated solvents when spent, including several carcinogens.',
    chemicals:
      'Toluene, methyl ethyl ketone (MEK), carbon disulfide, isobutanol, pyridine, benzene (as solvent), 2-ethoxyethanol, 2-nitropropane',
    criteria: [
      'The solvent was used for its solvent properties and is now SPENT',
      'IMPORTANT: Benzene is only F005 when used AS A SOLVENT. Unused benzene being discarded = U019 (U-list). Process waste containing benzene may be characteristic (D018)',
      'Includes still bottoms from recovery',
      'MIXTURE RULE: ≥10% of F005-listed solvents before use by volume triggers listing for the entire mixture',
      'MEK peroxide (a distinct compound from MEK) is not an F-code — it is U160 if unused and being discarded',
    ],
  },
  {
    id: 'f006',
    title: 'Electroplating WWT sludge',
    codes: ['F006'],
    cfr: '§261.31 F006',
    prompt:
      'Is this waste the wastewater treatment sludge from an electroplating operation (other than the six excluded processes listed below)?',
    description: 'The WWT sludge from treating rinse water and process effluent from most electroplating operations.',
    chemicals:
      'Wastewater treatment sludge containing plating metals — chromium, nickel, copper, zinc, cadmium, and treatment chemicals depending on the plating process',
    criteria: [
      'Must be the WASTEWATER TREATMENT SLUDGE — not the plating bath solution, not raw rinse water itself',
      'Six operations are EXCLUDED and do NOT generate F006: (1) sulfuric acid anodizing of aluminum, (2) tin plating on carbon steel, (3) zinc plating segregated on carbon steel, (4) aluminum or zinc-aluminum plating on carbon steel, (5) cleaning/stripping associated with those excluded platings, (6) chemical etching and milling of aluminum',
      'All other electroplating WWT sludges ARE F006 — including chromium, nickel, copper, gold, silver, and cadmium plating',
      'Rinse water itself is NOT F006; it becomes F006 once it passes through a WWT system and the sludge is removed',
    ],
  },
  {
    id: 'f007',
    title: 'Spent cyanide plating bath solution',
    codes: ['F007'],
    cfr: '§261.31 F007',
    prompt:
      'Is this waste the actual spent solution drained from a cyanide-based electroplating bath — the bath liquid itself when it is exhausted or being changed out?',
    description: 'The cyanide plating bath solution itself, when spent and removed from service.',
    chemicals:
      'The aqueous cyanide bath liquid — sodium or potassium cyanide complexes with plating metals such as gold, silver, copper, zinc, or cadmium',
    criteria: [
      'Must be the BATH SOLUTION ITSELF — the liquid that held the metal and cyanide complexing agents in the plating tank',
      'The plating bath must use CYANIDE as a complexing agent — non-cyanide baths (acid copper sulfate, sulfamate nickel, etc.) are NOT F007',
      'NOT rinse water from rinsing plated parts — that is ordinary wastewater (or F006 sludge after WWT)',
      'NOT the bottom sludge or residue from the bath — that is F008',
      'Applies when the bath is drained due to chemical exhaustion, contamination, or scheduled bath turnaround',
    ],
  },
  {
    id: 'f008',
    title: 'Cyanide plating bath bottom residues',
    codes: ['F008'],
    cfr: '§261.31 F008',
    prompt:
      'Is this waste the bottom sludge or solid residue physically removed from inside a cyanide plating bath tank when the tank is cleaned?',
    description:
      'The settled solids and residues removed from the bottom of cyanide plating bath tanks during tank cleaning.',
    chemicals:
      'Bottom sludge from cyanide plating baths — precipitated metal hydroxides, carbonates, drag-in contaminants, and other insoluble solids that accumulate at the tank floor',
    criteria: [
      'Must be the BOTTOM SLUDGE/RESIDUE from inside the plating bath tank — the settled solids on the tank floor',
      'The tank must be a CYANIDE-BASED plating bath',
      'NOT the bath solution (that is F007) — this is the physical solid material, not the liquid',
      'Generated when the tank is periodically cleaned out — e.g., during bath turnaround or annual tank cleaning',
      'Filter cake from filtering the BATH SOLUTION through an external filter is also F008 if from a cyanide bath',
    ],
  },
  {
    id: 'f009',
    title: 'Spent stripping/cleaning baths from cyanide electroplating',
    codes: ['F009'],
    cfr: '§261.31 F009',
    prompt:
      'Is this waste the spent solution from a cleaning or stripping bath that is part of a cyanide electroplating line — for example, spent soak cleaners or electrolytic cleaners used before plating?',
    description:
      'Spent stripping and cleaning bath solutions from electroplating lines where cyanide is used somewhere in the process.',
    chemicals:
      'Spent alkaline cleaning solutions, electrolytic cleaning baths, and anodic/cathodic cleaning baths on a cyanide plating line — when exhausted and being discarded',
    criteria: [
      'Must be a STRIPPING or CLEANING BATH SOLUTION — a solution used to prepare parts (cleaning, degreasing, descaling, stripping) within a cyanide electroplating operation',
      'The overall plating operation must use CYANIDE somewhere in the process line',
      'Must be SPENT — the bath is exhausted and being discarded as a liquid waste',
      'NOT the cyanide plating bath itself (F007) or its bottom residues (F008)',
      'Examples: alkaline soak cleaners, electrolytic cleaners (cathodic/anodic), and chemical strippers on a cyanide copper, gold, or silver plating line',
    ],
  },
  {
    id: 'f010',
    title: 'Oil bath quench residues from cyanide heat treating',
    codes: ['F010'],
    cfr: '§261.31 F010',
    prompt:
      'Is this waste the residue or sludge from an OIL BATH used to quench parts, where cyanide salts were used elsewhere in the same heat treating operation (e.g., the part was carburized in a cyanide salt bath before quenching)?',
    description: 'Quenching bath residues from oil baths at heat treating operations that use cyanide in the process.',
    chemicals:
      'Oil bath residue/sludge from quenching — contains quench oil breakdown products and drag-in from prior cyanide treatment steps',
    criteria: [
      'The quenching must have been done in an OIL BATH — not a water quench or polymer quench',
      'Cyanide salts must have been used SOMEWHERE in the same heat treating operation (e.g., the part went through a cyanide carburizing or case hardening step before the oil quench)',
      'It is the OIL BATH RESIDUE that is F010 — not the cyanide salt waste itself',
      'If cyanide is not used anywhere in the heat treating process, the oil bath residue is NOT F010',
      'Applies to liquid carburizing lines, carbonitriding, and cyanide case hardening that use an oil quench',
    ],
  },
  {
    id: 'f011',
    title: 'Spent cyanide from salt bath pot cleaning',
    codes: ['F011'],
    cfr: '§261.31 F011',
    prompt:
      'Is this waste the spent cyanide solution generated when periodically cleaning the salt bath pot (the vessel that holds the molten cyanide salt mixture)?',
    description: 'The spent cyanide-containing solution generated when the molten salt bath pot is cleaned out.',
    chemicals:
      'Spent aqueous cyanide solution from dissolving and rinsing the salt bath pot — contains residual sodium/potassium cyanide salts and heat treating byproducts',
    criteria: [
      'Must be the waste from CLEANING THE SALT BATH POT ITSELF — the vessel holding the molten cyanide salts used for liquid carburizing or case hardening',
      'Generated during periodic pot maintenance — not from the treated parts or their rinsing',
      'Must come from a cyanide-based SALT BATH heat treating process',
      'NOT the residue from oil bath quenching (that is F010)',
      'NOT the WWT sludge from treating quench wastewater (that is F012)',
    ],
  },
  {
    id: 'f012',
    title: 'Quench WWT sludge from cyanide heat treating',
    codes: ['F012'],
    cfr: '§261.31 F012',
    prompt:
      'Is this waste the wastewater treatment sludge from treating quench water at a heat treating operation that uses cyanide in the process?',
    description:
      'WWT sludge from treating wastewater generated during quenching operations at cyanide heat treating facilities.',
    chemicals:
      'WWT sludge from treating quench wastewater — contains metals and cyanide compounds dragged over from the cyanide heat treating step',
    criteria: [
      'Must be the WASTEWATER TREATMENT SLUDGE — specifically from treating the quench wastewater (not the quench oil residue itself)',
      'The heat treating operation must use CYANIDE in the process',
      'Generated when quench water (water used to cool parts or in water-based quenching) is treated to remove cyanide and metals before discharge',
      'NOT the oil bath residue (F010) or the salt bath pot cleaning waste (F011)',
    ],
  },
  {
    id: 'aluminum-conversion',
    title: 'Aluminum chemical conversion coating',
    codes: ['F019'],
    cfr: '§261.31 F019',
    prompt:
      'Does this facility apply a chemical conversion coating to aluminum surfaces (e.g., chromate conversion, Alodine, Iridite, non-chrome conversion)?',
    description: 'WWT sludges generated from the chemical conversion coating of aluminum.',
    chemicals:
      'Wastewater treatment sludges from chemical conversion coating of aluminum surfaces (chromate, trivalent chromate, non-chrome conversion coatings)',
    criteria: [
      'Must be a CHEMICAL CONVERSION COATING process on ALUMINUM (not steel, not paint adhesion prep for other metals)',
      'The listed waste is the WASTEWATER TREATMENT SLUDGE from treating process wastewater — not the coating solution itself or rinse water before treatment',
      'Exception: zirconium phosphating in ALUMINUM CAN WASHING where it is the EXCLUSIVE conversion coating process is NOT F019',
      'Special motor vehicle manufacturing exception: WWT sludges from zinc phosphating in auto manufacturing may qualify for alternative disposal if certain conditions are met — see §261.31(b)(4)',
      'Chromate conversion (Alodine/Iridite/Bonderite), trivalent chromate, and non-chrome organic conversion coatings all apply',
    ],
  },
  {
    id: 'f032',
    title: 'Wood preserving — chlorophenolic (PCP) operations',
    codes: ['F032'],
    cfr: '§261.31 F032',
    prompt:
      'Does this facility use or has it previously used chlorophenolic preservatives (pentachlorophenol/PCP or other chlorinated phenolics), and is this waste a process wastewater, drippage, or spent formulation from that operation?',
    description:
      'Process wastewaters, preservative drippage, and spent formulations from wood preserving operations using chlorophenolic compounds.',
    chemicals:
      'Chlorophenolic-contaminated process water, drippage from treated wood, spent PCP or chlorinated phenol preservative solutions',
    criteria: [
      'Applies to plants that CURRENTLY USE or have PREVIOUSLY USED chlorophenolic formulations — past use creates ongoing listing obligations even after switching preservatives',
      'Covered waste types: (a) process wastewater that came into contact with preservative, (b) preservative drippage from treated wood, (c) spent preservative formulations',
      'DOES NOT include K001 — the WWT bottom sediment sludge from this operation is K001, not F032',
      'Wastewaters that have NOT contacted process contaminants (e.g., stormwater fully segregated) are excluded',
      'Note: if a plant stopped using chlorophenolics and completed the §261.35 equipment cleaning/replacement procedure, F032 may no longer apply — confirm with your environmental counsel',
    ],
  },
  {
    id: 'f034',
    title: 'Wood preserving — creosote operations',
    codes: ['F034'],
    cfr: '§261.31 F034',
    prompt:
      'Does this facility use creosote as a wood preservative, and is this waste a process wastewater, drippage, or spent formulation from that operation?',
    description:
      'Process wastewaters, preservative drippage, and spent formulations from wood preserving operations using creosote.',
    chemicals:
      'Creosote-contaminated process water, drippage from creosote-treated wood, spent creosote solutions — includes petroleum-based, coal tar, and water-gas creosotes',
    criteria: [
      'The facility must use CREOSOTE as the preservative (petroleum, coal tar, or water-gas creosote)',
      'Covered waste types: (a) process wastewater that came into contact with creosote, (b) creosote drippage from treated wood, (c) spent creosote formulations',
      'DOES NOT include K001 — the WWT bottom sediment sludge is separately listed as K001',
      'Segregated non-contact wastewaters (e.g., clean stormwater) are excluded',
    ],
  },
  {
    id: 'f035',
    title: 'Wood preserving — arsenic/chromium inorganic preservatives',
    codes: ['F035'],
    cfr: '§261.31 F035',
    prompt:
      'Does this facility use inorganic preservatives containing arsenic or chromium (such as CCA, ACA, or ACZA), and is this waste a process wastewater, drippage, or spent formulation from that operation?',
    description:
      'Process wastewaters, preservative drippage, and spent formulations from wood preserving using arsenic or chromium-based inorganic preservatives.',
    chemicals:
      'CCA (chromated copper arsenate), ACA (ammoniacal copper arsenate), ACZA (ammoniacal copper zinc arsenate) — contaminated process water, drippage, and spent treatment solutions',
    criteria: [
      'The facility must use inorganic preservatives containing ARSENIC or CHROMIUM — CCA is by far the most common',
      'Covered waste types: (a) process wastewater that came into contact with the preservative solution, (b) drippage from treated wood, (c) spent inorganic preservative formulations',
      'DOES NOT include K001 — the WWT bottom sediment sludge is K001',
      'Note: CCA use in residential lumber has been largely phased out (post-2003) but industrial/commercial CCA-treating facilities still operate',
      'Segregated clean (non-contact) wastewaters are excluded',
    ],
  },
  {
    id: 'f037',
    title: 'Petroleum refinery primary oil/water separation sludge',
    codes: ['F037'],
    cfr: '§261.31 F037',
    prompt:
      'Is this waste the sludge from a PRIMARY (gravity-based) oil/water/solids separator at a petroleum refinery — such as an API separator, sump, impoundment, or stormwater unit receiving dry-weather flow?',
    description: 'Primary oil/water/solids separation sludge from petroleum refinery process wastewater treatment.',
    chemicals:
      'Bottom sludge from API separators, gravity impoundments, sumps, and conveyances treating petroleum refinery process wastewater',
    criteria: [
      'Must be from a PETROLEUM REFINERY (SIC 2911) — not a pipeline, terminal, distributor, or E&P facility',
      'Must be from a PRIMARY SEPARATION unit — gravity-based separation (API separators, ponds, impoundments, sumps, ditches, stormwater units receiving dry-weather flow)',
      'EXCLUDED: sludges from stormwater units that receive DRY-WEATHER FLOW ONLY (no process water); sludges from non-contact once-through cooling water segregated for treatment; sludges from aggressive biological treatment units (see §261.31 for the definition)',
      'The sludge is the BOTTOM SOLIDS layer — not the oil phase (skimmed oil) or the water phase',
    ],
  },
  {
    id: 'f038',
    title: 'Petroleum refinery secondary (emulsified) oil/water separation sludge',
    codes: ['F038'],
    cfr: '§261.31 F038',
    prompt:
      'Is this waste the sludge or float from a SECONDARY (physical/chemical) oil/water separation unit at a petroleum refinery — such as a dissolved air flotation (DAF) or induced air flotation (IAF) unit?',
    description:
      'Secondary oil/water/solids separation sludge and float from petroleum refinery process wastewater treatment.',
    chemicals:
      'Sludge and float from DAF, IAF, and similar physical/chemical treatment units processing emulsified oily wastewater from petroleum refining',
    criteria: [
      'Must be from a PETROLEUM REFINERY (SIC 2911)',
      'Must be from a SECONDARY SEPARATION unit that uses physical or chemical treatment to break oil/water emulsions — DAF (dissolved air flotation), IAF (induced air flotation), tank/impoundment chemical treatment',
      'Covers both the SLUDGE (settled solids) and the FLOAT (surface layer) from these units',
      'EXCLUDED: same exclusions as F037 — stormwater-only units, non-contact cooling water, aggressive biological treatment units',
      'The distinction from F037: F037 is gravity-based primary separation; F038 is physical/chemical treatment of the emulsified fraction that passes through primary separation',
    ],
  },
];

// K-List data
export interface KIndustry {
  id: string;
  title: string;
  question: string;
  qualifier: string;
  codes: { code: string; description: string; criteria: string }[];
}
export const K_LIST_INDUSTRIES: KIndustry[] = [
  {
    id: 'iron-steel',
    title: 'Iron & Steel Production (Electric Arc Furnace)',
    question:
      'Does your facility melt scrap steel in an electric arc furnace (EAF) or perform acid pickling/descaling of steel?',
    qualifier:
      'K-codes apply to primary steel producers using EAF technology and to steel finishing operations. BOF (basic oxygen furnace) and blast furnace routes do not generate K061.',
    codes: [
      {
        code: 'K061',
        description:
          'Emission control dust/sludge from primary steel production in electric arc furnaces — baghouse dust and wet scrubber sludge, typically containing zinc, lead, and cadmium from galvanized scrap',
        criteria:
          'Must come specifically from EAF-based primary steel production. The dust captured by the pollution control equipment (baghouse or wet scrubber) on the EAF off-gas system.',
      },
      {
        code: 'K062',
        description: 'Spent pickle liquor from steel finishing operations (SIC codes 331–332)',
        criteria:
          'The SPENT ACID used to remove mill scale from steel surfaces — typically spent HCl or H2SO4 pickling baths, once exhausted. Must be from a facility classified under SIC 331 (Blast Furnaces & Steel Mills) or 332 (Iron and Steel Foundries).',
      },
    ],
  },
  {
    id: 'petroleum',
    title: 'Petroleum Refining',
    question:
      'Is your facility classified as a petroleum refinery — converting crude oil or feedstocks into fuel products, lubricants, or petrochemicals?',
    qualifier:
      'Applies only to petroleum refineries (SIC 2911). Pipeline companies, tank farms, terminals, fuel blenders, and exploration/production facilities are NOT petroleum refineries under this listing.',
    codes: [
      {
        code: 'K048',
        description: 'Dissolved air flotation (DAF) float from petroleum refinery wastewater treatment',
        criteria:
          'The floating oil/grease layer and associated solids collected from a DAF unit treating refinery process wastewater.',
      },
      {
        code: 'K049',
        description: 'Slop oil emulsion solids from petroleum refining',
        criteria:
          'The solids/sludge that separate out when slop oil emulsions from refinery processes are treated or allowed to separate.',
      },
      {
        code: 'K050',
        description: 'Heat exchanger bundle cleaning sludge from petroleum refining',
        criteria:
          'The deposits and sludge removed when heat exchanger bundles are cleaned (hydroblasted, chemically cleaned, etc.) during refinery maintenance.',
      },
      {
        code: 'K051',
        description: 'API separator sludge from petroleum refining',
        criteria:
          'The bottom sludge layer that accumulates in American Petroleum Institute (API) gravity separators treating refinery process wastewater. Does not include the oil phase or the water phase.',
      },
      {
        code: 'K052',
        description: 'Tank bottoms (leaded) from petroleum refining',
        criteria:
          'Sludge from storage tanks that held leaded gasoline — the heavy sludge accumulated at the tank bottom. Less common now that leaded gasoline is phased out, but legacy tanks may still generate this.',
      },
      {
        code: 'K169',
        description: 'Crude oil storage tank sediment from petroleum refining operations',
        criteria:
          'The bottom sediment (BS&W — basic sediment and water) cleaned from crude oil storage tanks at the refinery. Generated during routine tank cleaning.',
      },
      {
        code: 'K170',
        description: 'Clarified slurry oil (CSO) tank sediment and/or in-line filter/separation solids',
        criteria:
          'Sediment from tanks storing clarified slurry oil (a heavy FCC byproduct), and solids from in-line filters on CSO streams.',
      },
      {
        code: 'K171',
        description:
          'Spent hydrotreating catalyst from petroleum refining (including guard beds used to desulfurize feeds)',
        criteria:
          'Catalyst from hydrotreating units (HDT, HDS, HCR) once it is spent and removed. Typically contains metals (Co, Mo, Ni, V). Does NOT include the inert support media.',
      },
      {
        code: 'K172',
        description: 'Spent hydrorefining catalyst from petroleum refining (including guard beds)',
        criteria:
          'Similar to K171 but from hydrorefining operations specifically. Does NOT include inert support media.',
      },
    ],
  },
  {
    id: 'primary-aluminum',
    title: 'Primary Aluminum Production',
    question:
      'Does your facility produce primary (virgin) aluminum via the Hall-Héroult electrolytic reduction process?',
    qualifier:
      'Applies only to primary smelters that reduce alumina to aluminum metal using electrolytic cells (pots). Secondary aluminum recycling operations do not generate K088.',
    codes: [
      {
        code: 'K088',
        description: 'Spent potliners from primary aluminum reduction',
        criteria:
          'The carbon lining removed from electrolytic reduction cells (pots) at the end of their useful life. These liners become impregnated with cryolite and other fluoride compounds and cyanides during the smelting process. VERY high fluoride content.',
      },
    ],
  },
  {
    id: 'secondary-lead',
    title: 'Secondary Lead Smelting',
    question: 'Does your facility melt and recover lead from recycled sources — primarily spent lead-acid batteries?',
    qualifier:
      'Applies to secondary lead smelters (recycled lead). Primary lead smelters (from ore concentrate) are NOT covered by K069/K100. The distinction is the feedstock: ore = primary; recycled materials (batteries, scrap) = secondary.',
    codes: [
      {
        code: 'K069',
        description: 'Emission control dust/sludge from secondary lead smelting',
        criteria:
          'The dust/sludge collected by pollution control equipment (baghouse, scrubber) on the secondary lead smelting furnace off-gas. Contains lead, arsenic, antimony, and other metals from battery paste and other scrap.',
      },
      {
        code: 'K100',
        description: 'Waste leaching solution from acid leaching of K069 emission control dust/sludge',
        criteria:
          'The spent acid solution generated when K069 dust/sludge is subjected to acid leaching to recover additional lead or other metals. This is the used leachate — not the solid residue.',
      },
    ],
  },
  {
    id: 'wood-preserving-k',
    title: 'Wood Preservation (K001)',
    question:
      'Does your facility treat wood using creosote and/or pentachlorophenol (PCP), and does it generate bottom sludge from wastewater treatment?',
    qualifier:
      'K001 is distinct from the F032/F034/F035 codes: K001 specifically covers the BOTTOM SEDIMENT SLUDGE from WWT of wood preserving wastewater. The wastewaters, drippage, and spent formulations are F-codes. Only apply K001 to the WWT sludge itself.',
    codes: [
      {
        code: 'K001',
        description:
          'Bottom sediment sludge from WWT of wood preserving processes using creosote and/or pentachlorophenol',
        criteria:
          'The sludge that settles to the bottom of wastewater treatment ponds, tanks, or lagoons treating process wastewater from creosote or PCP wood preserving. NOT the process wastewater itself (F034/F032), NOT drippage (F034/F032) — just the WWT sludge bottom sediment.',
      },
    ],
  },
  {
    id: 'inorganic-pigments',
    title: 'Inorganic Pigment Manufacturing',
    question:
      'Does your facility manufacture inorganic pigments — specifically chrome, molybdate, zinc, or iron-based pigments?',
    qualifier:
      'Applies to manufacturers of the specific pigment types listed. Pigment users (e.g., paint manufacturers mixing pre-made pigments) do not generate K-codes from pigment manufacturing, but may generate K086 (ink formulation).',
    codes: [
      {
        code: 'K002',
        description: 'WWT sludge from chrome yellow (PbCrO4) and chrome orange pigment production',
        criteria:
          'Wastewater treatment sludge from the production process for chrome yellow and chrome orange lead chromate pigments.',
      },
      {
        code: 'K003',
        description: 'WWT sludge from molybdate orange pigment production',
        criteria: 'WWT sludge from manufacturing molybdate orange (a mixed lead chromate/molybdate/sulfate pigment).',
      },
      {
        code: 'K004',
        description: 'WWT sludge from zinc yellow pigment production',
        criteria: 'WWT sludge from zinc chromate (zinc yellow) pigment manufacturing.',
      },
      {
        code: 'K005',
        description: 'WWT sludge from chrome green pigment production',
        criteria: 'WWT sludge from chrome green (a mixture of Prussian blue and chrome yellow) manufacturing.',
      },
      {
        code: 'K006',
        description: 'WWT sludge from chrome oxide green pigment production (anhydrous and hydrated)',
        criteria:
          'WWT sludge from the production of chrome oxide (Cr2O3) in either its anhydrous or hydrated (viridian) form.',
      },
      {
        code: 'K007',
        description: 'WWT sludge from iron blue (Milori blue / Prussian blue) pigment production',
        criteria: 'WWT sludge from iron blue (ferric ferrocyanide) pigment manufacturing.',
      },
      {
        code: 'K008',
        description: 'Oven residue from chrome oxide green pigment production',
        criteria:
          'The solid residue remaining in the oven/furnace after the chrome oxide green roasting step — not the WWT sludge (which is K006).',
      },
    ],
  },
  {
    id: 'coking',
    title: 'Coking / Coal Tar Processing',
    question:
      'Does your facility produce coke from coal, or refine/process coal tar into pitch, naphthalene, or other coal tar products?',
    qualifier:
      'Applies to coke ovens (converting coal to metallurgical coke) and to the byproduct recovery operations that collect and refine coal tar, light oil, ammonia, and other coke oven byproducts.',
    codes: [
      {
        code: 'K060',
        description: 'Ammonia still lime sludge from coking operations',
        criteria:
          'The calcium/magnesium sludge from the lime-based ammonia distillation process in coke oven byproduct recovery.',
      },
      {
        code: 'K087',
        description: 'Decanter tank tar sludge from coking operations',
        criteria:
          'The heavy sludge that settles in the tar decanter tank during coal tar separation in the coke oven byproduct plant.',
      },
      {
        code: 'K141',
        description: 'Process residues from coal tar recovery (stills, decanters, wash oil units)',
        criteria:
          'Residues from stills, decanters, and wash oil recovery units in the coal tar recovery and processing operations.',
      },
      {
        code: 'K142',
        description: 'Tar storage tank residues from coal tar production',
        criteria: 'Bottom residues that accumulate in tanks storing coal tar produced from coking operations.',
      },
      {
        code: 'K143',
        description: 'Process residues from light oil recovery (benzene/toluene/xylene recovery)',
        criteria:
          'Residues from the light oil recovery section of the coke oven byproduct plant, where BTX (benzene, toluene, xylene) and other light organics are recovered.',
      },
      {
        code: 'K144',
        description: 'Wastewater sump residues from light oil refining',
        criteria:
          'Sludge and residues from wastewater sumps and interceptors associated with the light oil refining operations.',
      },
      {
        code: 'K145',
        description: 'Residues from naphthalene collection and recovery operations',
        criteria:
          'Solid and liquid residues from the naphthalene collection, scrubbing, and recovery systems in coke byproduct operations.',
      },
      {
        code: 'K147',
        description: 'Tar storage tank residues from coal tar refining',
        criteria:
          'Tank bottom sludge from storage tanks holding coal tar in the coal tar refining (distillation) process — distinct from K142 which covers coal tar production.',
      },
      {
        code: 'K148',
        description: 'Residues from coal tar distillation (still bottoms)',
        criteria:
          'The heavy still-bottom residues remaining after coal tar is distilled into pitch, creosote oils, naphthalene, anthracene, and other fractions.',
      },
    ],
  },
  {
    id: 'explosives',
    title: 'Explosives Manufacturing',
    question:
      'Does your facility manufacture, formulate, load, or assemble explosives or explosive-containing products?',
    qualifier:
      'Applies to primary explosives manufacturers and formulation/loading facilities. Quarry and mining operations that USE explosives but do not manufacture them are not covered.',
    codes: [
      {
        code: 'K044',
        description: 'WWT sludges from manufacturing and processing of explosives',
        criteria:
          'Wastewater treatment sludges generated when treating process wastewater from explosives manufacturing (TNT, RDX, PETN, etc.).',
      },
      {
        code: 'K045',
        description: 'Spent carbon from treatment of wastewater containing explosives',
        criteria:
          'Activated carbon, granular or powdered, used to adsorb explosive compounds from wastewater — once the carbon is exhausted (spent).',
      },
      {
        code: 'K046',
        description: 'WWT sludges from lead-based initiating compound manufacturing/formulation/loading',
        criteria:
          'WWT sludges from operations using lead styphnate, lead azide, or other lead-based primary explosives in primers, detonators, or initiators.',
      },
      {
        code: 'K047',
        description: 'Pink/red water from TNT operations',
        criteria:
          'The pink- or red-colored wastewater generated when TNT comes in contact with alkaline water during processing, washout, or cleanup operations. Color is from TNT degradation products (trinitrobenzaldehyde, etc.).',
      },
    ],
  },
  {
    id: 'inorganic-chemicals',
    title: 'Inorganic Chemicals Production',
    question:
      'Does your facility produce chlorine, caustic soda, or antimony compounds using the processes described below?',
    qualifier:
      'These K-codes apply to specific production processes within the inorganic chemicals industry — only to the specific waste streams from those processes, not all wastes from a general chemicals facility.',
    codes: [
      {
        code: 'K071',
        description: 'Brine purification muds from mercury cell chlorine/caustic production',
        criteria:
          'The sludge from the brine purification step in mercury cell (Castner-Kellner) chlorine/caustic production. Only applies where separately pre-purified brine is NOT used. The mercury cell process is being phased out but legacy facilities still generate this.',
      },
      {
        code: 'K073',
        description: 'Chlorinated hydrocarbon waste from diaphragm cell chlorine production using graphite anodes',
        criteria:
          'The chlorinated organic waste from the purification column in the diaphragm cell process when graphite anodes (which react with chlorine to form chlorinated byproducts) are used. Does not apply to titanium-based dimensionally stable anodes.',
      },
      {
        code: 'K106',
        description: 'WWT sludge from mercury cell chlorine/caustic production',
        criteria:
          'The wastewater treatment sludge from treating process wastewater at a mercury cell chlorine/caustic plant. Contains mercury.',
      },
      {
        code: 'K176',
        description: 'Baghouse filters from antimony oxide production',
        criteria:
          'The collected dust in the baghouse air pollution control equipment at antimony oxide (Sb2O3) manufacturing facilities, including intermediate steps (antimony metal, crude antimony oxide production).',
      },
      {
        code: 'K177',
        description: 'Slag from antimony oxide production (speculatively accumulated or disposed)',
        criteria:
          'Slag generated during antimony oxide production that is either speculatively accumulated (stockpiled without a realistic plan for use) or disposed of. Slag being actively used beneficially is not K177.',
      },
      {
        code: 'K178',
        description: 'Residues from ferric chloride manufacturing via the chloride-ilmenite process for TiO2',
        criteria:
          'Process residues generated during the production of ferric chloride as a byproduct of the chloride-ilmenite route to titanium dioxide (TiO2). Formed during acid recovery from the off-gas.',
      },
    ],
  },
  {
    id: 'pesticides',
    title: 'Pesticide Manufacturing',
    question:
      'Does your facility manufacture active pesticide ingredients — specifically any of the compounds listed below (chlordane, toxaphene, phorate, disulfoton, 2,4-D, etc.)?',
    qualifier:
      'Applies to the MANUFACTURER of the active ingredient, not to formulators who blend pre-made active ingredients into pesticidal products. K-code designation is tied to the specific synthetic process, not just pesticide use.',
    codes: [
      {
        code: 'K031',
        description: 'By-product salts from MSMA and cacodylic acid production',
        criteria:
          'The salts generated as byproducts in the chemical synthesis of monosodium methanearsonate (MSMA) or cacodylic acid (dimethylarsinic acid) — organoarsenic herbicides.',
      },
      {
        code: 'K032',
        description: 'WWT sludge from chlordane production',
        criteria:
          'Wastewater treatment sludge from the manufacturing of chlordane (an organochlorine insecticide). Production has largely ceased in the US, but legacy waste may still exist.',
      },
      {
        code: 'K033',
        description: 'Wastewater and scrub water from cyclopentadiene chlorination in chlordane production',
        criteria:
          'The aqueous waste streams from the chlorination reactor and scrubber systems in the chlordane synthesis process.',
      },
      {
        code: 'K034',
        description: 'Filter solids from hexachlorocyclopentadiene (HCCPD) filtration in chlordane production',
        criteria: 'Solid filter cake from purifying hexachlorocyclopentadiene, a chlordane precursor.',
      },
      {
        code: 'K035',
        description: 'WWT sludges from creosote production (not wood preserving — this is chemical manufacturing)',
        criteria:
          'WWT sludge from the CHEMICAL PRODUCTION of creosote as a commercial product. Distinct from K001 (wood preserving WWT) and F034 (wood preserving process wastes).',
      },
      {
        code: 'K036',
        description: 'Still bottoms from toluene reclamation distillation in disulfoton production',
        criteria:
          'Distillation residues (still bottoms) from recovering and recycling toluene used as a solvent in the synthesis of disulfoton (an organophosphate insecticide).',
      },
      {
        code: 'K037',
        description: 'WWT sludges from disulfoton production',
        criteria:
          'Wastewater treatment sludge generated from treating process wastewater at a disulfoton manufacturing facility.',
      },
      {
        code: 'K038',
        description: 'Wastewater from phorate washing and stripping',
        criteria:
          'Process wastewater from the washing and stripping steps in phorate (an organophosphate insecticide) synthesis.',
      },
      {
        code: 'K039',
        description: 'Filter cake from diethylphosphorodithioic acid (DEPDTA) filtration in phorate production',
        criteria:
          'The solid filter cake from removing impurities from the DEPDTA intermediate used in phorate synthesis.',
      },
      {
        code: 'K040',
        description: 'WWT sludge from phorate production',
        criteria: 'Wastewater treatment sludge from treating process wastewater at a phorate manufacturing facility.',
      },
      {
        code: 'K041',
        description: 'WWT sludge from toxaphene production',
        criteria:
          'WWT sludge from the chlorination of camphene to produce toxaphene (a chlorinated insecticide). US production has ceased.',
      },
      {
        code: 'K042',
        description: 'Heavy ends from tetrachlorobenzene distillation in 2,4,5-T production',
        criteria:
          'The high-boiling residue from distilling tetrachlorobenzene in the synthesis of 2,4,5-trichlorophenoxyacetic acid (2,4,5-T). May contain dioxins/furans.',
      },
      {
        code: 'K043',
        description: '2,6-Dichlorophenol waste from 2,4-D production',
        criteria:
          'The waste 2,6-dichlorophenol generated as a byproduct during the alkaline hydrolysis step in 2,4-dichlorophenoxyacetic acid (2,4-D) herbicide production.',
      },
      {
        code: 'K097',
        description: 'Vacuum stripper discharge from chlordane chlorinator',
        criteria:
          'Off-gas condensate or liquid discharge from the vacuum stripping system on the chlordane chlorination reactor.',
      },
      {
        code: 'K098',
        description: 'Untreated process wastewater from toxaphene production',
        criteria:
          'Process wastewater from toxaphene manufacturing that has not yet been treated — the raw process effluent.',
      },
      {
        code: 'K099',
        description: 'Untreated wastewater from 2,4-D production',
        criteria: 'The raw (untreated) process wastewater from 2,4-D herbicide manufacturing.',
      },
    ],
  },
  {
    id: 'vet-pharma',
    title: 'Veterinary Pharmaceuticals (Arsenic-based)',
    question:
      'Does your facility manufacture veterinary pharmaceutical products derived from arsenic or organoarsenic compounds — such as roxarsone, arsanilic acid, or similar products?',
    qualifier:
      'Very narrow category. Applies to manufacturers using arsenic or organoarsenic chemistry to produce veterinary drugs (historically used as growth promoters in poultry/swine feed). Not applicable to general pharmaceutical manufacturing.',
    codes: [
      {
        code: 'K084',
        description: 'WWT sludges from veterinary pharmaceutical production using arsenic or organo-arsenic compounds',
        criteria:
          'Wastewater treatment sludge from treating process wastewater at veterinary pharmaceutical plants where arsenic chemistry is part of the active ingredient synthesis.',
      },
      {
        code: 'K101',
        description:
          'Distillation tar residues from aniline-based compound distillation (veterinary pharma from arsenic)',
        criteria:
          'The heavy tar residues from distilling aniline-derived intermediates used in organoarsenic veterinary drug synthesis.',
      },
      {
        code: 'K102',
        description: 'Activated carbon decolorization residue from veterinary pharma from arsenic',
        criteria:
          'Spent activated carbon used for decolorizing or purifying aniline-based intermediates or products in arsenic-based veterinary pharmaceutical production.',
      },
    ],
  },
  {
    id: 'ink-dyes',
    title: 'Ink Formulation / Dyes & Pigments Manufacturing',
    question:
      'Does your facility formulate inks (mixing pigments, driers, soaps, stabilizers containing chromium or lead), or manufacture dyes/pigments in the azo, triarylmethane, perylene, or anthraquinone chemical classes?',
    qualifier:
      'K086 applies to ink formulators using Cr/Pb-containing ingredients. K181 applies specifically to manufacturers (not users) of listed dye/pigment chemical classes — with quantity-based thresholds for whether the listing applies.',
    codes: [
      {
        code: 'K086',
        description:
          'Solvent, caustic, and water washes/sludges from ink formulation equipment containing chromium or lead',
        criteria:
          'The wash solutions and sludges generated when cleaning tanks, mixers, and other equipment used to formulate inks — ONLY if those inks contained chromium- or lead-based pigments, driers, soaps, or stabilizers as ingredients.',
      },
      {
        code: 'K181',
        description:
          'Nonwastewaters from dyes/pigments production (azo, triarylmethane, perylene, anthraquinone classes) at or above constituent loading thresholds',
        criteria:
          'Applies only if: (1) your facility manufactures dyes/pigments in the listed chemical classes (azo, triarylmethane/triphenylmethane, perylene, anthraquinone), AND (2) the nonwastewater waste stream meets or exceeds the constituent mass loading thresholds in 40 CFR 261.32(c). Review the specific threshold table — this is NOT a blanket listing for all dye/pigment manufacturers.',
      },
    ],
  },
];

// P-list and U-list chemical data
export interface ListedChem {
  list: 'P' | 'U';
  code: string;
  name: string;
  cas?: string;
  category: string;
  note?: string;
}
export const LISTED_CHEMICALS: ListedChem[] = [
  // P-list: Cyanides
  { list: 'P', code: 'P030', name: 'Cyanides (soluble cyanide salts, NOS)', category: 'Cyanides' },
  { list: 'P', code: 'P063', name: 'Hydrogen cyanide (HCN)', cas: '74-90-8', category: 'Cyanides' },
  { list: 'P', code: 'P098', name: 'Potassium cyanide (KCN)', cas: '151-50-8', category: 'Cyanides' },
  { list: 'P', code: 'P106', name: 'Sodium cyanide (NaCN)', cas: '143-33-9', category: 'Cyanides' },
  { list: 'P', code: 'P021', name: 'Calcium cyanide', cas: '592-01-8', category: 'Cyanides' },
  { list: 'P', code: 'P029', name: 'Copper cyanide', cas: '544-92-3', category: 'Cyanides' },
  { list: 'P', code: 'P074', name: 'Nickel cyanide', cas: '557-19-7', category: 'Cyanides' },
  { list: 'P', code: 'P121', name: 'Zinc cyanide', cas: '557-21-1', category: 'Cyanides' },
  { list: 'P', code: 'P104', name: 'Silver cyanide', cas: '506-64-9', category: 'Cyanides' },
  { list: 'P', code: 'P013', name: 'Barium cyanide', cas: '542-62-1', category: 'Cyanides' },
  { list: 'P', code: 'P031', name: 'Cyanogen', cas: '460-19-5', category: 'Cyanides' },
  { list: 'P', code: 'P033', name: 'Cyanogen chloride', cas: '506-77-4', category: 'Cyanides' },
  { list: 'P', code: 'P099', name: 'Potassium silver cyanide', cas: '506-61-6', category: 'Cyanides' },
  // P-list: Arsenic & Metals
  { list: 'P', code: 'P010', name: 'Arsenic acid (H3AsO4)', cas: '7778-39-4', category: 'Arsenic & Metal Compounds' },
  {
    list: 'P',
    code: 'P011',
    name: 'Arsenic pentoxide (As2O5)',
    cas: '1303-28-2',
    category: 'Arsenic & Metal Compounds',
  },
  {
    list: 'P',
    code: 'P012',
    name: 'Arsenic trioxide (As2O3)',
    cas: '1327-53-3',
    category: 'Arsenic & Metal Compounds',
  },
  { list: 'P', code: 'P015', name: 'Beryllium powder', cas: '7440-41-7', category: 'Arsenic & Metal Compounds' },
  {
    list: 'P',
    code: 'P073',
    name: 'Nickel carbonyl Ni(CO)4',
    cas: '13463-39-3',
    category: 'Arsenic & Metal Compounds',
  },
  { list: 'P', code: 'P110', name: 'Tetraethyl lead', cas: '78-00-2', category: 'Arsenic & Metal Compounds' },
  { list: 'P', code: 'P113', name: 'Thallium oxide (Tl2O3)', cas: '1314-32-5', category: 'Arsenic & Metal Compounds' },
  { list: 'P', code: 'P115', name: 'Thallium(I) sulfate', cas: '7446-18-6', category: 'Arsenic & Metal Compounds' },
  {
    list: 'P',
    code: 'P087',
    name: 'Osmium tetroxide (OsO4)',
    cas: '20816-12-0',
    category: 'Arsenic & Metal Compounds',
  },
  { list: 'P', code: 'P119', name: 'Ammonium vanadate', cas: '7803-55-6', category: 'Arsenic & Metal Compounds' },
  {
    list: 'P',
    code: 'P120',
    name: 'Vanadium pentoxide (V2O5)',
    cas: '1314-62-1',
    category: 'Arsenic & Metal Compounds',
  },
  // P-list: Industrial toxic gases/chemicals
  { list: 'P', code: 'P095', name: 'Phosgene (COCl2)', cas: '75-44-5', category: 'Industrial Toxic Chemicals' },
  { list: 'P', code: 'P096', name: 'Phosphine (PH3)', cas: '7803-51-2', category: 'Industrial Toxic Chemicals' },
  { list: 'P', code: 'P064', name: 'Methyl isocyanate (MIC)', cas: '624-83-9', category: 'Industrial Toxic Chemicals' },
  { list: 'P', code: 'P076', name: 'Nitric oxide (NO)', cas: '10102-43-9', category: 'Industrial Toxic Chemicals' },
  {
    list: 'P',
    code: 'P078',
    name: 'Nitrogen dioxide (NO2)',
    cas: '10102-44-0',
    category: 'Industrial Toxic Chemicals',
  },
  { list: 'P', code: 'P056', name: 'Fluorine (F2)', cas: '7782-41-4', category: 'Industrial Toxic Chemicals' },
  { list: 'P', code: 'P105', name: 'Sodium azide', cas: '26628-22-8', category: 'Industrial Toxic Chemicals' },
  {
    list: 'P',
    code: 'P054',
    name: 'Ethyleneimine (aziridine)',
    cas: '151-56-4',
    category: 'Industrial Toxic Chemicals',
  },
  { list: 'P', code: 'P068', name: 'Methyl hydrazine', cas: '60-34-4', category: 'Industrial Toxic Chemicals' },
  { list: 'P', code: 'P022', name: 'Carbon disulfide', cas: '75-15-0', category: 'Industrial Toxic Chemicals' },
  {
    list: 'P',
    code: 'P081',
    name: 'Nitroglycerine',
    cas: '55-63-0',
    category: 'Industrial Toxic Chemicals',
    note: 'Reactive (R)',
  },
  {
    list: 'P',
    code: 'P112',
    name: 'Tetranitromethane',
    cas: '509-14-8',
    category: 'Industrial Toxic Chemicals',
    note: 'Reactive (R)',
  },
  {
    list: 'P',
    code: 'P082',
    name: 'N-Nitrosodimethylamine (NDMA)',
    cas: '62-75-9',
    category: 'Industrial Toxic Chemicals',
  },
  // P-list: Organophosphate insecticides
  { list: 'P', code: 'P089', name: 'Parathion', cas: '56-38-2', category: 'Organophosphate Insecticides' },
  { list: 'P', code: 'P071', name: 'Methyl parathion', cas: '298-00-0', category: 'Organophosphate Insecticides' },
  { list: 'P', code: 'P039', name: 'Disulfoton', cas: '298-04-4', category: 'Organophosphate Insecticides' },
  { list: 'P', code: 'P094', name: 'Phorate', cas: '298-02-2', category: 'Organophosphate Insecticides' },
  {
    list: 'P',
    code: 'P041',
    name: 'Diethyl-p-nitrophenyl phosphate (Paraoxon)',
    cas: '311-45-5',
    category: 'Organophosphate Insecticides',
  },
  { list: 'P', code: 'P044', name: 'Dimethoate', cas: '60-51-5', category: 'Organophosphate Insecticides' },
  { list: 'P', code: 'P097', name: 'Famphur', cas: '52-85-7', category: 'Organophosphate Insecticides' },
  // P-list: Carbamate pesticides
  { list: 'P', code: 'P127', name: 'Carbofuran', cas: '1563-66-2', category: 'Carbamate Pesticides' },
  { list: 'P', code: 'P066', name: 'Methomyl', cas: '16752-77-5', category: 'Carbamate Pesticides' },
  { list: 'P', code: 'P070', name: 'Aldicarb', cas: '116-06-3', category: 'Carbamate Pesticides' },
  // P-list: Chlorinated insecticides
  { list: 'P', code: 'P059', name: 'Heptachlor', cas: '76-44-8', category: 'Chlorinated Insecticides' },
  { list: 'P', code: 'P004', name: 'Aldrin', cas: '309-00-2', category: 'Chlorinated Insecticides' },
  { list: 'P', code: 'P037', name: 'Dieldrin', cas: '60-57-1', category: 'Chlorinated Insecticides' },
  { list: 'P', code: 'P050', name: 'Endosulfan', cas: '115-29-7', category: 'Chlorinated Insecticides' },
  { list: 'P', code: 'P051', name: 'Endrin', cas: '72-20-8', category: 'Chlorinated Insecticides' },
  { list: 'P', code: 'P123', name: 'Toxaphene', cas: '8001-35-2', category: 'Chlorinated Insecticides' },
  // P-list: Rodenticides/other
  {
    list: 'P',
    code: 'P001',
    name: 'Warfarin (>0.3%)',
    cas: '81-81-2',
    category: 'Rodenticides & Other',
    note: 'Warfarin at ≤0.3% is U248',
  },
  { list: 'P', code: 'P108', name: 'Strychnine & salts', cas: '57-24-9', category: 'Rodenticides & Other' },
  {
    list: 'P',
    code: 'P122',
    name: 'Zinc phosphide (>10%)',
    cas: '1314-84-7',
    category: 'Rodenticides & Other',
    note: 'Zinc phosphide at ≤10% is U249',
  },
  {
    list: 'P',
    code: 'P075',
    name: 'Nicotine & salts (>10%; not FDA-approved NRT)',
    cas: '54-11-5',
    category: 'Rodenticides & Other',
  },
  { list: 'P', code: 'P042', name: 'Epinephrine (adrenaline)', cas: '51-43-4', category: 'Rodenticides & Other' },
  // U-list: Solvents
  { list: 'U', code: 'U002', name: 'Acetone (2-propanone)', cas: '67-64-1', category: 'Solvents' },
  { list: 'U', code: 'U019', name: 'Benzene', cas: '71-43-2', category: 'Solvents' },
  { list: 'U', code: 'U044', name: 'Chloroform (trichloromethane)', cas: '67-66-3', category: 'Solvents' },
  { list: 'U', code: 'U080', name: 'Methylene chloride (DCM)', cas: '75-09-2', category: 'Solvents' },
  { list: 'U', code: 'U159', name: 'Methyl ethyl ketone (MEK, 2-butanone)', cas: '78-93-3', category: 'Solvents' },
  { list: 'U', code: 'U161', name: 'Methyl isobutyl ketone (MIBK)', cas: '108-10-1', category: 'Solvents' },
  { list: 'U', code: 'U220', name: 'Toluene', cas: '108-88-3', category: 'Solvents' },
  { list: 'U', code: 'U239', name: 'Xylene (mixed isomers)', cas: '1330-20-7', category: 'Solvents' },
  { list: 'U', code: 'U117', name: 'Ethyl ether (diethyl ether)', cas: '60-29-7', category: 'Solvents' },
  { list: 'U', code: 'U112', name: 'Ethyl acetate', cas: '141-78-6', category: 'Solvents' },
  { list: 'U', code: 'U213', name: 'Tetrahydrofuran (THF)', cas: '109-99-9', category: 'Solvents' },
  { list: 'U', code: 'U056', name: 'Cyclohexane', cas: '110-82-7', category: 'Solvents' },
  { list: 'U', code: 'U057', name: 'Cyclohexanone', cas: '108-94-1', category: 'Solvents' },
  { list: 'U', code: 'U228', name: 'Trichloroethylene (TCE)', cas: '79-01-6', category: 'Solvents' },
  { list: 'U', code: 'U210', name: 'Tetrachloroethylene (PCE)', cas: '127-18-4', category: 'Solvents' },
  { list: 'U', code: 'U226', name: '1,1,1-Trichloroethane (methyl chloroform)', cas: '71-55-6', category: 'Solvents' },
  { list: 'U', code: 'U211', name: 'Carbon tetrachloride', cas: '56-23-5', category: 'Solvents' },
  { list: 'U', code: 'U037', name: 'Chlorobenzene', cas: '108-90-7', category: 'Solvents' },
  { list: 'U', code: 'U154', name: 'Methanol', cas: '67-56-1', category: 'Solvents' },
  { list: 'U', code: 'U031', name: 'n-Butyl alcohol (1-butanol)', cas: '71-36-3', category: 'Solvents' },
  { list: 'U', code: 'U140', name: 'Isobutyl alcohol', cas: '78-83-1', category: 'Solvents' },
  { list: 'U', code: 'U055', name: 'Cumene (isopropylbenzene)', cas: '98-82-8', category: 'Solvents' },
  // U-list: Industrial chemicals
  { list: 'U', code: 'U122', name: 'Formaldehyde', cas: '50-00-0', category: 'Industrial Chemicals' },
  {
    list: 'U',
    code: 'U134',
    name: 'Hydrogen fluoride (hydrofluoric acid)',
    cas: '7664-39-3',
    category: 'Industrial Chemicals',
  },
  { list: 'U', code: 'U188', name: 'Phenol', cas: '108-95-2', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U115', name: 'Ethylene oxide', cas: '75-21-8', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U009', name: 'Acrylonitrile', cas: '107-13-1', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U007', name: 'Acrylamide', cas: '79-06-1', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U169', name: 'Nitrobenzene', cas: '98-95-3', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U012', name: 'Aniline', cas: '62-53-3', category: 'Industrial Chemicals' },
  {
    list: 'U',
    code: 'U133',
    name: 'Hydrazine',
    cas: '302-01-2',
    category: 'Industrial Chemicals',
    note: 'Reactive (R) and toxic',
  },
  {
    list: 'U',
    code: 'U223',
    name: 'Toluene diisocyanate (TDI)',
    cas: '26471-62-5',
    category: 'Industrial Chemicals',
    note: 'Reactive (R) and toxic',
  },
  { list: 'U', code: 'U108', name: '1,4-Dioxane', cas: '123-91-1', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U196', name: 'Pyridine', cas: '110-86-1', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U103', name: 'Dimethyl sulfate', cas: '77-78-1', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U041', name: 'Epichlorohydrin', cas: '106-89-8', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U115', name: 'Ethylene oxide', cas: '75-21-8', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U008', name: 'Acrylic acid', cas: '79-10-7', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U162', name: 'Methyl methacrylate', cas: '80-62-6', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U003', name: 'Acetonitrile', cas: '75-05-8', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U001', name: 'Acetaldehyde', cas: '75-07-0', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U190', name: 'Phthalic anhydride', cas: '85-44-9', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U147', name: 'Maleic anhydride', cas: '108-31-6', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U123', name: 'Formic acid', cas: '64-18-6', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U135', name: 'Hydrogen sulfide (H2S)', cas: '7783-06-4', category: 'Industrial Chemicals' },
  {
    list: 'U',
    code: 'U160',
    name: 'Methyl ethyl ketone peroxide (MEKP)',
    cas: '1338-23-4',
    category: 'Industrial Chemicals',
    note: 'Reactive (R)',
  },
  { list: 'U', code: 'U098', name: '1,1-Dimethylhydrazine (UDMH)', cas: '57-14-7', category: 'Industrial Chemicals' },
  { list: 'U', code: 'U151', name: 'Mercury (elemental)', cas: '7439-97-6', category: 'Industrial Chemicals' },
  // U-list: Chlorinated compounds
  { list: 'U', code: 'U070', name: 'o-Dichlorobenzene', cas: '95-50-1', category: 'Chlorinated Compounds' },
  { list: 'U', code: 'U071', name: 'm-Dichlorobenzene', cas: '541-73-1', category: 'Chlorinated Compounds' },
  { list: 'U', code: 'U072', name: 'p-Dichlorobenzene', cas: '106-46-7', category: 'Chlorinated Compounds' },
  {
    list: 'U',
    code: 'U077',
    name: 'Ethylene dichloride (1,2-dichloroethane)',
    cas: '107-06-2',
    category: 'Chlorinated Compounds',
  },
  {
    list: 'U',
    code: 'U076',
    name: '1,1-Dichloroethane (ethylidene dichloride)',
    cas: '75-34-3',
    category: 'Chlorinated Compounds',
  },
  {
    list: 'U',
    code: 'U078',
    name: '1,1-Dichloroethylene (vinylidene chloride)',
    cas: '75-35-4',
    category: 'Chlorinated Compounds',
  },
  { list: 'U', code: 'U079', name: '1,2-Dichloroethylene', cas: '156-60-5', category: 'Chlorinated Compounds' },
  {
    list: 'U',
    code: 'U083',
    name: '1,2-Dichloropropane (propylene dichloride)',
    cas: '78-87-5',
    category: 'Chlorinated Compounds',
  },
  {
    list: 'U',
    code: 'U075',
    name: 'Dichlorodifluoromethane (CFC-12)',
    cas: '75-71-8',
    category: 'Chlorinated Compounds',
  },
  {
    list: 'U',
    code: 'U121',
    name: 'Trichlorofluoromethane (CFC-11)',
    cas: '75-69-4',
    category: 'Chlorinated Compounds',
  },
  { list: 'U', code: 'U227', name: '1,1,2-Trichloroethane', cas: '79-00-5', category: 'Chlorinated Compounds' },
  { list: 'U', code: 'U208', name: '1,1,1,2-Tetrachloroethane', cas: '630-20-6', category: 'Chlorinated Compounds' },
  { list: 'U', code: 'U209', name: '1,1,2,2-Tetrachloroethane', cas: '79-34-5', category: 'Chlorinated Compounds' },
  { list: 'U', code: 'U043', name: 'Vinyl chloride', cas: '75-01-4', category: 'Chlorinated Compounds' },
  {
    list: 'U',
    code: 'U066',
    name: '1,2-Dibromo-3-chloropropane (DBCP)',
    cas: '96-12-8',
    category: 'Chlorinated Compounds',
  },
  { list: 'U', code: 'U067', name: 'Ethylene dibromide (EDB)', cas: '106-93-4', category: 'Chlorinated Compounds' },
  // U-list: Phenols & Aromatics
  { list: 'U', code: 'U052', name: 'Cresol / Cresylic acid', cas: '1319-77-3', category: 'Phenols & Aromatics' },
  { list: 'U', code: 'U101', name: '2,4-Dimethylphenol', cas: '105-67-9', category: 'Phenols & Aromatics' },
  { list: 'U', code: 'U081', name: '2,4-Dichlorophenol', cas: '120-83-2', category: 'Phenols & Aromatics' },
  { list: 'U', code: 'U048', name: 'o-Chlorophenol', cas: '95-57-8', category: 'Phenols & Aromatics' },
  { list: 'U', code: 'U165', name: 'Naphthalene', cas: '91-20-3', category: 'Phenols & Aromatics' },
  { list: 'U', code: 'U105', name: '2,4-Dinitrotoluene', cas: '121-14-2', category: 'Phenols & Aromatics' },
  { list: 'U', code: 'U106', name: '2,6-Dinitrotoluene', cas: '606-20-2', category: 'Phenols & Aromatics' },
  // U-list: Other
  {
    list: 'U',
    code: 'U248',
    name: 'Warfarin (≤0.3%)',
    cas: '81-81-2',
    category: 'Other',
    note: 'Warfarin >0.3% is P001',
  },
  {
    list: 'U',
    code: 'U249',
    name: 'Zinc phosphide (≤10%)',
    cas: '1314-84-7',
    category: 'Other',
    note: 'Zinc phosphide >10% is P122',
  },
  { list: 'U', code: 'U113', name: 'Ethyl acrylate', cas: '140-88-5', category: 'Other' },
  { list: 'U', code: 'U192', name: 'Pronamide (Kerb)', cas: '23950-58-5', category: 'Other' },
  { list: 'U', code: 'U240', name: '2,4-D, salts & esters', cas: '94-75-7', category: 'Other' },
];
